'use client'

import * as React from 'react'
import { useInView } from 'framer-motion'
import { cn } from '@/lib/utils'

// SectionReveal — the homepage's scroll-reveal primitive. The visual work
// is done by the `.section-reveal` / `.is-visible` CSS in redesign-theme.css
// (fade-up + settle scale, plus the ancestor-gated `.stagger-in` children),
// so this component only owns the trigger. It now uses Framer Motion's
// `useInView` instead of a hand-rolled IntersectionObserver: same contract
// (reveal once, just before fully in view), less code, one motion runtime.
//
// Reduced motion is handled in CSS (`@media (prefers-reduced-motion)` nulls
// the transform/opacity), so content is always visible regardless of the
// trigger.
//
//   <SectionReveal>
//     <YourSection />
//   </SectionReveal>

export function SectionReveal({
  children,
  delay = 0,
  className,
}: {
  children: React.ReactNode
  /** Milliseconds to delay the reveal, for staggering nested reveals.
   *  Keep under ~400ms total; longer feels laggy. */
  delay?: number
  className?: string
}) {
  const ref = React.useRef<HTMLDivElement>(null)
  const inView = useInView(ref, { once: true, margin: '0px 0px -10% 0px' })

  return (
    <div
      ref={ref}
      className={cn('section-reveal', inView && 'is-visible', className)}
      style={delay ? { transitionDelay: `${delay}ms` } : undefined}
    >
      {children}
    </div>
  )
}
